import { useState } from "react";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Header } from "./Header";
import { useVisitPdfs } from "CustomHooks/useVisitPdfs";
import Table from "Components/Common/Table/Table";
import { VisitPdfsTableHeading, VisitPdfsTableBody, VISIT_PDFS_COLUMN_COUNT } from "./VisitPdfsTable";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { PaginationBtns } from "Components/Common/PaginationBtns/PaginationBtns";
import { ErrorPopUp } from "Components/Common/PopUp/PopUp";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { Button } from "Components/Common/Button/Button";
import "Colors.css";

const ContentModal = ({ open, content, onClose }) => (
  <Transition.Root show={open} as={Fragment}>
    <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={onClose}>
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div className="inline-block align-bottom bg-white rounded-lg px-6 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
            <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Note Content
            </Dialog.Title>
            <div className="mt-2 max-h-96 overflow-y-auto">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
            </div>
            <div className="mt-5">
              <Button
                variant="transparent"
                className="text-sm font-medium sapphire bg-transparent border border-gray-200 py-2 px-4 inline-flex rounded-md"
                btnText="Close"
                isDisabled={false}
                onClick={onClose}
              />
            </div>
          </div>
        </Transition.Child>
      </div>
    </Dialog>
  </Transition.Root>
);

export const VisitPdfs = ({ setActiveView, patientId, visitId, docName }) => {
  const [apiError, setApiError] = useState(false);
  const [notice, setNotice] = useState(false);
  const [contentModal, setContentModal] = useState({ open: false, content: "" });

  const currentVisitId = visitId || "";
  const currentPatientId = patientId || "";

  const { pdfs, isLoading, isError, error, hasNext, hasPrevious, goToNextPage, goToPreviousPage } = useVisitPdfs(
    currentVisitId,
    currentPatientId,
  );

  const displayError = apiError || (isError ? errorFormatter(error?.message) : false);

  const handleViewPdfError = (message) => {
    setApiError(message);
  };

  const handleInvalidateError = (message) => {
    setApiError(message);
  };

  const handleCopied = (message) => {
    setNotice(message);
  };

  const handleViewContent = (content) => {
    setContentModal({ open: true, content });
  };

  return (
    <>
      {displayError && <ErrorPopUp error={displayError} setApiError={setApiError} />}
      {notice && <ErrorPopUp msg={notice} setApiError={setNotice} />}

      <Header onBack={setActiveView} title={docName}>
        <span className="light_grayish_navy text-sm">Visit Notes PDFs</span>
      </Header>

      <Table
        heading={<VisitPdfsTableHeading />}
        body={
          !isLoading && pdfs.length > 0 ? (
            <VisitPdfsTableBody
              data={pdfs}
              visitId={currentVisitId}
              patientId={currentPatientId}
              onViewContent={handleViewContent}
              onViewPdfError={handleViewPdfError}
              onInvalidateError={handleInvalidateError}
              onCopied={handleCopied}
            />
          ) : (
            !isLoading && (
              <tr>
                <td colSpan={VISIT_PDFS_COLUMN_COUNT} className="text-center py-10 text-gray-400 bg-white">
                  No PDFs Found
                </td>
              </tr>
            )
          )
        }
      />

      {isLoading && <Spinner />}


      <ContentModal open={contentModal.open} content={contentModal.content} onClose={() => setContentModal({ open: false, content: "" })} />
    </>
  );
};
